import React, { useState } from 'react';
import type { CSSProperties } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import './styles.css';
import { BottomNav } from './components/BottomNav';
import { useTravelBuddyUi } from './store/travelBuddyUiStore';
import { sf } from '../themeConfig/sisuForgeTokens';
import shieldUrl from '../assets/sisuforge/sisu-forge-shield.png';
import { getSisuForgeHubHref, isSisuForgeHubExternal } from '../sisuForgeHub';

const PUBLIC = process.env.PUBLIC_URL ?? '';
const WORDMARK_SRC = `${PUBLIC}/sisu-forge-wordmark.svg`;

const shellStyle: CSSProperties = {
  color: sf.text,
  minHeight: '100vh',
  width: '100vw',
  maxWidth: '100vw',
  marginLeft: 'calc(50% - 50vw)',
  marginRight: 'calc(50% - 50vw)',
  marginTop: 0,
  marginBottom: 0,
  padding: 0,
  isolation: 'isolate',
  position: 'relative',
  fontFamily: sf.fontSans,
  letterSpacing: 'normal',
  textShadow: 'none',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  boxSizing: 'border-box',
};

const innerStyle: CSSProperties = {
  maxWidth: 560,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
  boxSizing: 'border-box',
  fontSize: 'clamp(16px, 2.8vw, 17px)',
  lineHeight: 1.55,
  position: 'relative',
  zIndex: 1,
};

const heroStyle: CSSProperties = {
  padding: 'clamp(12px, 4vw, 20px) clamp(14px, 4vw, 20px) 8px',
  borderBottom: `1px solid ${sf.border}`,
  background: `linear-gradient(180deg, ${sf.surface} 0%, rgba(18, 18, 18, 0.4) 100%)`,
};

export default function TravelBuddyShell() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: 1 },
        },
      })
  );

  const modalOpen = useTravelBuddyUi((s) => s.modalOpen);

  const hubHref = getSisuForgeHubHref();
  const hubExternal = isSisuForgeHubExternal(hubHref);

  const brandLinkStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(10px, 3vw, 16px)',
    flexWrap: 'wrap',
    justifyContent: 'center',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="travel-buddy-root" style={shellStyle}>
        <header style={heroStyle}>
          {hubExternal ? (
            <a
              href={hubHref}
              style={brandLinkStyle}
              aria-label="Sisu Forge — back to hub"
            >
              <img
                src={shieldUrl}
                alt=""
                width={56}
                height={56}
                style={{
                  width: 'clamp(44px, 12vw, 56px)',
                  height: 'auto',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <img
                src={WORDMARK_SRC}
                alt="Sisu Forge"
                style={{
                  height: 'clamp(28px, 9vw, 40px)',
                  width: 'auto',
                  maxWidth: 'min(100%, 280px)',
                  objectFit: 'contain',
                }}
              />
            </a>
          ) : (
            <RouterLink
              to={hubHref}
              style={brandLinkStyle}
              aria-label="Sisu Forge — back to hub"
            >
              <img
                src={shieldUrl}
                alt=""
                width={56}
                height={56}
                style={{
                  width: 'clamp(44px, 12vw, 56px)',
                  height: 'auto',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <img
                src={WORDMARK_SRC}
                alt="Sisu Forge"
                style={{
                  height: 'clamp(28px, 9vw, 40px)',
                  width: 'auto',
                  maxWidth: 'min(100%, 280px)',
                  objectFit: 'contain',
                }}
              />
            </RouterLink>
          )}
          <p
            style={{
              margin: '12px 0 0',
              fontFamily: sf.fontDisplay,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: sf.textSubtle,
              textAlign: 'center',
            }}
          >
            Travel Buddy
          </p>
        </header>
        <div style={innerStyle}>
          <Outlet />
        </div>
        {!modalOpen && <BottomNav />}
      </div>
    </QueryClientProvider>
  );
}
